import React from "react";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { BaseTabs } from "components/Shared/BaseTabs";

import { useAppMeta, useAuth, useTablePagination } from "hooks";
import { useLazyListAdjustmentQuery } from "store/services/PaymentsApi/adjustmentApi";
import HighlightedText from "components/Shared/HighlightedText";

const AdjustmentListScreen = () => {
  const { isVendor } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Adjustments" }];

  const adjustmentTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Adjustment ID",
      selector: (row) => row?.adjustmentID,
      cell: (row) => {
        const displayId = row?.adjustmentID;
        const path = !isVendor
          ? `/app/crm/adjustment/${row?._id}/detail`
          : `/app/vendor/adjustment/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Account",
      selector: (row) => row?.account?.name,
      cell: (row) => row?.account?.name || "N/A",
    },
    {
      name: "Entity",
      selector: (row) => row?.entity?.name,
      cell: (row) => row?.entity?.name || "N/A",
    },
    {
      name: "Profile Name",
      selector: (row) => row?.sourceModelRef?.name || "N/A",
      cell: (row) => {
        const name = row?.sourceModelRef?.name || "N/A";
        switch (row?.sourceModel) {
          case "VendorProfile":
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{name}</span>
                <small style={{ color: "gray" }}>vendor</small>
              </div>
            );
          case "Customer":
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{name}</span>
                <small style={{ color: "gray" }}>customer</small>
              </div>
            );
          case "Contractor":
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>{name}</span>
                <small style={{ color: "gray" }}>contractor</small>
              </div>
            );
          default:
            return name;
        }
      },
    },
    {
      name: "Amount",
      selector: (row) => row?.amount,
      cell: (row) => row?.amount?.toFixed(2) || "N/A",
    },
    {
      name: "Added By",
      selector: (row) => row?.addedBy.name,
      cell: (row) => row?.addedBy.name,
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              !isVendor
                ? `/app/crm/adjustment/${row?._id}/detail`
                : `/app/vendor/adjustment/${row?._id}/detail`
            }
          >
            <IconButton
              size="small"
              aria-label="View Adjustment"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          {!isVendor && (
            <Link to={`/app/crm/adjustment/${row?._id}/edit`}>
              <IconButton size="small" aria-label="Edit Adjustment">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>
          )}
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchAdjustments,
    { data: adjustmentRes, isLoading, isFetching, error },
  ] = useLazyListAdjustmentQuery();

  React.useEffect(() => {
    fetchAdjustments({
      active: activeTab === "Active" ? "true" : "false",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchAdjustments, keyword, perPage]);

  useAppMeta(`${activeTab} Adjustments`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <BaseTabs tabs={adjustmentTabs} defaultValue={activeTab} />

        <TableFilter
          searchLabel="Adjustment ID"
          disabled={isLoading || isFetching}
        >
          <div className="font-medium"></div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={adjustmentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={adjustmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default AdjustmentListScreen;

const PageActions = () => {
  const { isVendor } = useAuth();

  return (
    <>
      {!isVendor && (
        <Link to="/app/crm/adjustment/create">
          <Button startIcon={<Add />} variant="contained" color="primary">
            Add Adjustment
          </Button>
        </Link>
      )}
    </>
  );
};
